/**
 * TaskBoard — TASK-685.
 *
 * The columns-by-status rendering of the SAME query `TaskListPage` already runs. It holds no
 * data of its own: the only board-local state is which card is currently being dragged, so
 * there is nothing here that can disagree with the list view.
 *
 * The board only becomes a feature because of the `review` column (TASK-684) — a board over
 * pending/in_progress/completed is a re-skin of the list that already existed. `review` is
 * the one column that says "work the AI already finished, waiting on you".
 *
 * Drag-and-drop is native HTML5 (`draggable` + dragstart/dragover/drop). No dnd library:
 * it is ~15 lines and a dependency for that is not worth its weight. `onDragOver` MUST
 * `preventDefault()` or the browser refuses the drop — that is the entire cross-browser
 * subtlety. The card's own status control stays the non-drag path, which is also the
 * keyboard/a11y path, so nothing here is drag-only.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, Clock, Sparkles, Undo2, XCircle } from 'lucide-react';

import TaskCard from './TaskCard';
import type { TaskOutcomeSubmission } from './TaskOutcomePicker';
import type { Task, TaskStatus } from '../../types/tasks';

interface Column {
  status: TaskStatus;
  label: string;
  icon: React.ReactNode;
  headerClass: string;
}

/** The four lifecycle columns — always shown, in flow order. */
const COLUMNS: Column[] = [
  {
    status: 'pending',
    label: 'To do',
    icon: <Clock className="w-4 h-4 text-gray-500" />,
    headerClass: 'bg-gray-50 border-gray-200',
  },
  {
    status: 'in_progress',
    label: 'In progress',
    icon: <AlertCircle className="w-4 h-4 text-blue-600" />,
    headerClass: 'bg-blue-50 border-blue-200',
  },
  {
    // Amber and named for the human, not the machine: this column is a request.
    status: 'review',
    label: 'Needs your sign-off',
    icon: <Sparkles className="w-4 h-4 text-amber-600" />,
    headerClass: 'bg-amber-50 border-amber-200',
  },
  {
    // TASK-744: the reviewer sent it back — the producer owes a revision. Without this
    // column a sent-back task vanishes from the board.
    status: 'changes_requested',
    label: 'Changes requested',
    icon: <Undo2 className="w-4 h-4 text-orange-600" />,
    headerClass: 'bg-orange-50 border-orange-200',
  },
  {
    status: 'completed',
    label: 'Done',
    icon: <CheckCircle className="w-4 h-4 text-green-600" />,
    headerClass: 'bg-green-50 border-green-200',
  },
];

/** `cancelled` is not work in flight, so it gets no standing column — but with the list view
 *  gone (TASK-685 review) a soft-deleted task would otherwise disappear from the product
 *  entirely. Shown only when the current result set actually contains one, i.e. when the RM
 *  filtered for it or just cancelled something. */
const CANCELLED_COLUMN: Column = {
  status: 'cancelled',
  label: 'Cancelled',
  icon: <XCircle className="w-4 h-4 text-gray-400" />,
  headerClass: 'bg-gray-100 border-gray-300',
};

export interface TaskBoardProps {
  tasks: Task[];
  /** Moves a task to a new status. The caller owns the PATCH (and its optimistic locking). */
  onStatusChange: (task: Task, status: TaskStatus) => void | Promise<void>;
  /** Task to scroll to and ring — where `/tasks?highlight=<id>` lands the RM. */
  highlightId?: string | null;
  onComplete?: (taskId: string, submission?: TaskOutcomeSubmission) => void | Promise<unknown>;
  onSnooze?: (taskId: string, snoozeUntil: string) => void;
  onEdit?: (task: Task) => void;
}

const TaskBoard: React.FC<TaskBoardProps> = ({
  tasks,
  onStatusChange,
  highlightId,
  onComplete,
  onSnooze,
  onEdit,
}) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<TaskStatus | null>(null);
  const highlightRef = useRef<HTMLDivElement | null>(null);

  // The board is four columns wide; a highlighted card can easily be below the fold.
  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlightId, tasks]);

  const columns = tasks.some((task) => task.status === 'cancelled')
    ? [...COLUMNS, CANCELLED_COLUMN]
    : COLUMNS;

  const clearDrag = () => {
    setDraggingId(null);
    setOverColumn(null);
  };

  const handleDrop = (status: TaskStatus) => {
    const task = tasks.find((t) => t.id === draggingId);
    clearDrag();
    // Dropping a card back where it started is not a transition — PATCHing it would write a
    // status_changed event (TASK-684's trigger) for a move that never happened.
    if (!task || task.status === status) return;
    void onStatusChange(task, status);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="task-board">
      {columns.map((column) => {
        const columnTasks = tasks.filter((task) => task.status === column.status);
        return (
          <div
            key={column.status}
            data-testid={`task-column-${column.status}`}
            onDragOver={(e) => {
              // Without preventDefault the browser rejects the drop outright.
              e.preventDefault();
              if (overColumn !== column.status) setOverColumn(column.status);
            }}
            onDragLeave={() => setOverColumn((prev) => (prev === column.status ? null : prev))}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(column.status);
            }}
            className={`
              rounded-lg border transition-colors
              ${overColumn === column.status ? 'border-[#002B45] bg-[#002B45]/5' : 'border-gray-200 bg-white'}
            `}
          >
            <div className={`flex items-center gap-2 px-3 py-2 rounded-t-lg border-b ${column.headerClass}`}>
              {column.icon}
              <h3 className="text-sm font-semibold text-gray-900">{column.label}</h3>
              <span
                data-testid={`task-column-count-${column.status}`}
                className="ml-auto text-xs font-medium text-gray-600 bg-white/70 rounded-full px-2 py-0.5"
              >
                {columnTasks.length}
              </span>
            </div>

            <div className="p-3 space-y-3 min-h-[8rem]">
              {columnTasks.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">Nothing here</p>
              ) : (
                columnTasks.map((task) => (
                  <div
                    key={task.id}
                    ref={task.id === highlightId ? highlightRef : undefined}
                    draggable
                    data-testid={`task-draggable-${task.id}`}
                    data-highlighted={task.id === highlightId ? 'true' : undefined}
                    onDragStart={() => setDraggingId(task.id)}
                    // Fires on a cancelled drag (Esc / invalid target) as well as a
                    // completed one, so the drag state can never be left stuck.
                    onDragEnd={clearDrag}
                    className={`cursor-grab active:cursor-grabbing rounded-lg ${
                      draggingId === task.id ? 'opacity-50' : ''
                    } ${task.id === highlightId ? 'ring-2 ring-[#002B45] ring-offset-2' : ''}`}
                  >
                    <TaskCard
                      task={task}
                      onComplete={onComplete}
                      onSnooze={onSnooze}
                      onEdit={onEdit}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TaskBoard;
export { COLUMNS as TASK_BOARD_COLUMNS };
